import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    TextField,
    InputAdornment,
    Typography,
    Box,
    Chip,
    Pagination,
    CircularProgress,
    Divider
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewIcon, FileDownload as DownloadIcon, Check as SelectIcon } from '@mui/icons-material';
import { templateService, LessonPlanTemplate } from '../services/templateService';

interface TemplateSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (template: LessonPlanTemplate) => void;
}

export const TemplateSelectionDialog: React.FC<TemplateSelectionDialogProps> = ({ open, onClose, onSelect }) => {
    const [templates, setTemplates] = useState<LessonPlanTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const limit = 5;

    useEffect(() => {
        if (open) {
            loadTemplates();
        }
    }, [open, page, searchQuery]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const res = await templateService.getAll({
                page,
                limit,
                search: searchQuery,
                is_active: true
            });
            if (res && res.data) {
                setTemplates(res.data);
                setTotalPages(Math.ceil((res.total || 0) / limit));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Chọn Mẫu Giáo Án</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Tìm kiếm mẫu..."
                        value={searchQuery}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List>
                        {templates.map((template) => (
                            <React.Fragment key={template.id}>
                                <ListItem alignItems="flex-start">
                                    <ListItemText
                                        primary={template.title}
                                        secondary={
                                            <React.Fragment>
                                                <Typography variant="body2" color="text.primary">
                                                    {template.subject_name || 'Môn học: Chung'}
                                                </Typography>
                                                {" — " + (template.description || "Không có mô tả")}
                                            </React.Fragment>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => onSelect(template)}
                                            startIcon={<SelectIcon />}
                                        >
                                            Sử dụng
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                        {templates.length === 0 && (
                            <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                                Không tìm thấy mẫu nào.
                            </Typography>
                        )}
                    </List>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, p) => setPage(p)}
                        color="primary"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Đóng</Button>
            </DialogActions>
        </Dialog>
    );
};
